import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Plots.css'; // Reusing Plots styles for consistent grid layout

import { supabase } from '../supabaseClient';


export default function Commercial() {
  const navigate = useNavigate();
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const fetchCommercials = async () => {
      setLoading(true);
      const { data } = await supabase.from('properties').select('*').eq('category', 'commercial');
      if (data) setProperties(data);
      setLoading(false);
    };
    fetchCommercials();
  }, []);

  return (
    <div className="plots-page">
      {/* Page Header */}
      <section className="page-header animate-fade-in-up">
        <div className="container text-center">
          <span className="section-tag" style={{ backgroundColor: 'var(--color-secondary-light)', color: 'var(--color-secondary)' }}>
            Commercial Spaces
          </span>
          <h1 className="page-title">Premium Commercial Properties</h1>
          <p className="page-subtitle">
            Invest in world-class commercial properties and mixed-use developments designed to elevate business presence and maximize returns.
          </p>
        </div>
      </section>

      {/* Grid List */}
      <section className="plots-grid-section">
        <div className="container">
          <div className="grid-3" style={{ alignItems: 'stretch' }}>
            {loading ? (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '40px 0' }}>
                <h3 style={{ color: 'var(--text-secondary)' }}>Loading...</h3>
              </div>
            ) : properties.length === 0 ? (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '40px 0' }}>
                <h3 style={{ color: 'var(--text-secondary)' }}>No commercial properties found.</h3>
              </div>
            ) : properties.map((project) => (
              <div key={project.id} className="premium-card plot-card flex-column" onClick={() => setSelectedPlot(project)} style={{ cursor: 'pointer' }}>
                <div className="plot-card-image-container">
                  <img src={project.image_url || project.image} alt={project.title} className="plot-card-image" />
                  <div className="plot-card-badges">
                    <span className="approval-badge">✓ {project.approval}</span>
                    <span className={`status-badge status-plots`} style={project.status === "Ready to Move In" ? { backgroundColor: '#ffca28', color: '#333', fontWeight: 'bold', textShadow: 'none' } : {}}>
                      {project.status}
                    </span>
                  </div>
                </div>

                <div className="plot-card-body">
                  <h2 className="plot-title">{project.title}</h2>
                  <p className="plot-location">📍 {project.location}</p>

                  <div className="plot-specs">
                    <div className="spec-item">
                      <span className="spec-label">Unit Sizes</span>
                      <span className="spec-val font-bold">{project.sizes}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Price Guide</span>
                      <span className="spec-val highlight-val">{project.price}</span>
                    </div>
                  </div>

                  <div className="plot-card-footer" style={{ marginTop: 'auto', paddingTop: '15px' }}>
                    <button
                      className="btn btn-primary w-full"
                      style={{ marginBottom: '8px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPlot(project);
                      }}
                    >
                      View Complete Info
                    </button>
                    <button
                      className="btn btn-secondary w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.localStorage.setItem('enquiry_subject', `Enquiry about commercial property at ${project.title}`);
                        navigate('/contact');
                      }}
                    >
                      Check Availability & Prices
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedPlot && (
        <div className="modal-overlay" onClick={() => setSelectedPlot(null)}>
          <div className="modal-content animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPlot(null)}>&times;</button>
            <img 
              src={selectedPlot.image_url || selectedPlot.image} 
              alt={selectedPlot.title} 
              className="modal-image" 
              style={{ maxHeight: '550px', width: '100%', objectFit: 'cover', objectPosition: 'center' }}
            />
            
            <div className="modal-body">
               <h2 className="modal-title">{selectedPlot.title}</h2>
               <p className="modal-tagline">{selectedPlot.tagline}</p>
              
               {selectedPlot.amenities?.length > 0 && (
                 <div className="modal-section">
                   <h3>Amenities & Features</h3>
                   <ul className="grid-list">
                     {selectedPlot.amenities.map((feat, i) => <li key={i}>▪ {feat}</li>)}
                   </ul>
                 </div>
               )}

               {selectedPlot.extended_details?.locationHighlights?.length > 0 && (
                 <div className="modal-section">
                   <h3>Location & Connectivity</h3>
                   <ul className="grid-list">
                     {selectedPlot.extended_details.locationHighlights.map((loc, i) => <li key={i}>📍 {loc}</li>)}
                   </ul>
                 </div>
               )}

               {selectedPlot.extended_details?.highlights?.length > 0 && (
                 <div className="modal-section">
                   <h3>Project Highlights</h3>
                   <ul>
                     {selectedPlot.extended_details.highlights.map((h, i) => <li key={i}>✓ {h}</li>)}
                   </ul>
                 </div>
               )}

               {selectedPlot.extended_details?.fullAmenities?.length > 0 && (
                 <div className="modal-section">
                   <h3>Diverse Mixed-Use Facilities</h3>
                   <ul className="grid-list">
                     {selectedPlot.extended_details.fullAmenities.map((a, i) => <li key={i}>▪ {a}</li>)}
                   </ul>
                 </div>
               )}

               {selectedPlot.extended_details?.extraInfo?.length > 0 && (
                 <div className="modal-section">
                   <h3>{selectedPlot.extended_details.extraInfoTitle}</h3>
                   <ul className="grid-list">
                     {selectedPlot.extended_details.extraInfo.map((l, i) => (
                       <li key={i}>{selectedPlot.extended_details.extraInfoIcon} {l}</li>
                     ))}
                   </ul>
                 </div>
               )}
               
               {selectedPlot.extended_details?.bookingInfo?.length > 0 && (
                 <div className="modal-section">
                   <h3>{selectedPlot.extended_details.bookingTitle}</h3>
                   <ul className="grid-list">
                     {selectedPlot.extended_details.bookingInfo.map((l, i) => (
                       <li key={i}>{selectedPlot.extended_details.bookingIcon} {l}</li>
                     ))}
                   </ul>
                 </div>
               )}
               
               {selectedPlot.extended_details?.partnerInfo?.length > 0 && (
                 <div className="modal-section">
                   <h3>{selectedPlot.extended_details.partnerTitle}</h3>
                   <ul className="grid-list">
                     {selectedPlot.extended_details.partnerInfo.map((l, i) => (
                       <li key={i}>{selectedPlot.extended_details.partnerIcon} {l}</li>
                     ))}
                   </ul>
                 </div>
               )}

               {selectedPlot.extended_details?.driversInfo?.length > 0 && (
                 <div className="modal-section">
                   <h3>{selectedPlot.extended_details.driversTitle}</h3>
                   <ul className="grid-list">
                     {selectedPlot.extended_details.driversInfo.map((l, i) => (
                       <li key={i}>{selectedPlot.extended_details.driversIcon} {l}</li>
                     ))}
                   </ul>
                 </div>
               )}

              <div className="modal-footer text-center">
                 <button
                   className="btn btn-secondary w-full"
                   onClick={() => {
                     window.localStorage.setItem('enquiry_subject', `Enquiry about commercial property at ${selectedPlot.title}`);
                     setSelectedPlot(null);
                     navigate('/contact');
                   }}
                 >
                   Enquire Now
                 </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
